import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  FileText,
  Calendar,
  Building2,
  Upload as UploadIcon,
  Clock,
  CheckCircle,
  AlertCircle,
  RefreshCw,
  Newspaper,
  ExternalLink,
  Trash2,
  Wifi,
  WifiOff,
  Tags,
  Globe,
} from 'lucide-react';
import { getAnnouncement, triggerProcessing, getAnnouncementSourcePdfUrl, deleteArticle, getArticlePublishTargets, getAnnouncementPipelineJobs } from '../lib/api/index.js';
import type { PipelineJobSummary } from '../lib/api/types.js';
import { format } from 'date-fns';
import type { ProcessingStatus } from '../lib/api/types.js';
import { RegenerateArticleDialog } from '../components/RegenerateArticleDialog.js';
import { ArticleProgressCard } from '../components/ArticleProgressCard.js';
import { AnnouncementContactsCard } from '../components/AnnouncementContactsCard.js';
import { ConsolidateControl } from '../components/ConsolidateControl.js';
import { useWebSocket } from '../hooks/useWebSocket.js';
import { useArticleGeneration } from '../hooks/useArticleGeneration.js';
import { Modal, ModalBody, ModalFooter } from '../components/ui/modal.js';

const statusColors: Record<ProcessingStatus, string> = {
  uploading: 'bg-grey-300 text-white',
  uploaded: 'bg-grey-300 text-white',
  queued: 'bg-blue-500 text-white',
  extracting: 'bg-yellow-500 text-white',
  enriching: 'bg-orange-500 text-white',
  generating: 'bg-purple-500 text-white',
  completed: 'bg-green-500 text-white',
  failed: 'bg-red-500 text-white',
};

const statusIcons: Record<ProcessingStatus, React.ReactNode> = {
  uploading: <UploadIcon className="h-5 w-5" />,
  uploaded: <UploadIcon className="h-5 w-5" />,
  queued: <Clock className="h-5 w-5" />,
  extracting: <RefreshCw className="h-5 w-5 animate-spin" />,
  enriching: <RefreshCw className="h-5 w-5 animate-spin" />,
  generating: <RefreshCw className="h-5 w-5 animate-spin" />,
  completed: <CheckCircle className="h-5 w-5" />,
  failed: <AlertCircle className="h-5 w-5" />,
};

export function AnnouncementDetailPage() {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const [showReprocessConfirm, setShowReprocessConfirm] = useState(false);
  const [forceExtractImages, setForceExtractImages] = useState(false);
  const [showRegenerateDialog, setShowRegenerateDialog] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<{ articleId: string; articleTitle: string } | null>(null);

  // Connect to WebSocket for real-time updates
  // Matches the error broadcast channel: assayerai:announcement:{id}
  const { connected } = useWebSocket({
    channels: [`assayerai:announcement:${id}`],
    onEvent: {
      status_change: (data) => {
        console.log('📡 WebSocket: Status changed:', data);
        // Auto-invalidation will trigger UI update
      },
      article_created: (data) => {
        console.log('📡 WebSocket: Article created:', data);
        // Auto-invalidation will trigger UI update
      },
      processing_error: (data) => {
        console.error('📡 WebSocket: Processing error:', data);
        // Auto-invalidation will trigger UI update
      },
    },
    autoInvalidate: true,
    debug: true, // Enable for testing
  });

  // Track article generation in progress
  const { generatingArticles, startGenerating } = useArticleGeneration({
    announcementId: id!,
    enabled: !!id,
  });

  const { data: announcement, isLoading, error } = useQuery({
    queryKey: ['announcement', id],
    queryFn: () => getAnnouncement(id!),
    enabled: !!id,
    // Removed polling - using WebSocket for real-time updates
  });

  const { data: pipelineJobs } = useQuery({
    queryKey: ['announcement-pipeline-jobs', id],
    queryFn: () => getAnnouncementPipelineJobs(id!),
    enabled: !!id,
  });

  // Fetch PDF URL for the source document
  const { data: pdfData } = useQuery({
    queryKey: ['announcement-pdf', id],
    queryFn: () => getAnnouncementSourcePdfUrl(id!),
    enabled: !!id,
  });

  // Fetch publish targets for all articles in this announcement
  const articleIds = announcement?.articles?.map((a: any) => a.article_id) || [];
  const { data: articlePublishTargets } = useQuery({
    queryKey: ['announcement-article-targets', id, articleIds.join(',')],
    queryFn: async () => {
      const results: Record<string, Array<{ target_name: string; destination_type: string | null }>> = {};
      await Promise.all(
        articleIds.map(async (articleId: string) => {
          try {
            const targets = await getArticlePublishTargets(articleId);
            if (targets?.length) {
              results[articleId] = targets.map((t: any) => ({
                target_name: t.target_name,
                destination_type: t.destination_name?.includes('Production') ? 'main_branch' : 'test_branch',
              }));
            }
          } catch { /* ignore individual failures */ }
        })
      );
      return results;
    },
    enabled: articleIds.length > 0,
  });

  const triggerMutation = useMutation({
    mutationFn: ({ announcementId, force, extractImages }: { announcementId: string; force: boolean; extractImages: boolean }) =>
      triggerProcessing(announcementId, force, extractImages),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['announcement', id] });
      setShowReprocessConfirm(false);
      setForceExtractImages(false);
    },
    onError: (error: any) => {
      console.error('Processing trigger failed:', error);
      alert(`Failed to start processing: ${error.message || 'Unknown error'}`);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (articleId: string) => deleteArticle(articleId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['announcement', id] });
      setDeleteConfirm(null);
    },
    onError: (error: any) => {
      console.error('Delete failed:', error);
      alert(`Failed to delete article: ${error.message || 'Unknown error'}`);
    },
  });

  const handleTriggerProcessing = (force: boolean = false) => {
    if (id) {
      triggerMutation.mutate({ announcementId: id, force, extractImages: forceExtractImages });
    }
  };

  const handleDeleteClick = (e: React.MouseEvent, article: any) => {
    e.preventDefault();
    e.stopPropagation();
    setDeleteConfirm({
      articleId: article.article_id,
      articleTitle: article.article_title,
    });
  };

  const confirmDelete = () => {
    if (deleteConfirm) {
      deleteMutation.mutate(deleteConfirm.articleId);
    }
  };

  const handleRegenerateSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['announcement', id] });
    setShowRegenerateDialog(false);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="inline-block h-10 w-10 animate-spin rounded-full border-4 border-solid border-primary-500 border-r-transparent"></div>
          <p className="mt-6 text-grey-600 font-medium">Loading announcement...</p>
        </div>
      </div>
    );
  }

  if (error || !announcement) {
    return (
      <div className="text-center py-16">
        <AlertCircle className="h-16 w-16 text-danger-500 mx-auto mb-4" />
        <h2 className="text-2xl font-bold text-grey-900 mb-2">Announcement Not Found</h2>
        <p className="text-base text-grey-600 mb-6">
          {error ? (error as Error).message : 'The announcement could not be found'}
        </p>
        <Link
          to="/announcements"
          className="inline-flex items-center space-x-2 text-primary-600 hover:text-primary-700 font-semibold"
        >
          <ArrowLeft className="h-4 w-4" />
          <span>Back to Announcements</span>
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-8 max-w-6xl mx-auto px-4 py-6">
      {/* Header */}
      <div>
        <Link
          to="/announcements"
          className="inline-flex items-center space-x-2 text-primary-600 hover:text-primary-700 font-semibold mb-6 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          <span>Back to Announcements</span>
        </Link>
        <h1 className="text-2xl sm:text-4xl font-bold text-grey-900">{announcement.announcement_title}</h1>
      </div>

      {/* Status Card */}
      <div className="bg-surface rounded-xl border border-grey-200 shadow-md p-4 sm:p-8">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-2">
          <div className="flex items-center space-x-3">
            <h2 className="text-xl font-bold text-grey-900">Processing Status</h2>
            {/* WebSocket Connection Indicator */}
            <div
              className={`flex items-center space-x-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
                connected
                  ? 'bg-green-100 text-green-700'
                  : 'bg-grey-100 text-grey-500'
              }`}
              title={connected ? 'Live updates connected' : 'Connecting...'}
            >
              {connected ? (
                <>
                  <Wifi className="h-3.5 w-3.5" />
                  <span>Live</span>
                </>
              ) : (
                <>
                  <WifiOff className="h-3.5 w-3.5" />
                  <span>Connecting</span>
                </>
              )}
            </div>
          </div>
          <div className="flex items-center space-x-3">
            {statusIcons[announcement.processing_status]}
            <span
              className={`px-4 py-2 text-sm font-semibold rounded-lg ${
                statusColors[announcement.processing_status]
              }`}
            >
              {announcement.processing_status.toUpperCase()}
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <InfoItem
            icon={<Building2 className="h-5 w-5" />}
            label={['research_markdown','research_pdf','editorial_brief'].includes((announcement as any).source_type) && announcement.company_code === 'PENDING' ? 'Type' : 'Company'}
            value={['research_markdown','research_pdf','editorial_brief'].includes((announcement as any).source_type) && announcement.company_code === 'PENDING'
              ? ((announcement as any).source_type === 'editorial_brief' ? 'Editorial brief (no issuer)' : 'Research report (no issuer)')
              : `${announcement.company_code} - ${announcement.company_name}`}
          />
          <InfoItem
            icon={<Calendar className="h-5 w-5" />}
            label="Announcement Date"
            value={format(new Date(announcement.announcement_date), 'MMMM d, yyyy')}
          />
          <InfoItem
            icon={<UploadIcon className="h-5 w-5" />}
            label="Uploaded"
            value={format(new Date(announcement.uploaded_at), 'MMMM d, yyyy HH:mm')}
          />
          <InfoItem
            icon={<FileText className="h-5 w-5" />}
            label="Uploaded By"
            value={announcement.uploaded_by}
          />
          {announcement.processing_completed_at && (
            <InfoItem
              icon={<CheckCircle className="h-5 w-5" />}
              label="Last Processed"
              value={format(new Date(announcement.processing_completed_at), 'MMMM d, yyyy HH:mm')}
            />
          )}
          {pdfData?.pdf_url && (
            <div className="flex items-start space-x-3">
              <div className="text-grey-400 mt-0.5">
                <ExternalLink className="h-5 w-5" />
              </div>
              <div>
                <p className="text-sm text-grey-500 font-medium mb-1">Source Document</p>
                {/* A markdown research report has two readable artefacts: the
                    source as submitted, and the typeset PDF the network
                    renders from it — the document a reader gets from the
                    published article. Both are offered; a PDF upload has only
                    itself. */}
                <div className="flex flex-col space-y-1.5">
                  <a
                    href={pdfData.pdf_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    download={pdfData.source_filename}
                    className="text-base font-semibold text-primary-600 hover:text-primary-700 inline-flex items-center space-x-1 transition-colors"
                  >
                    <span>
                      {pdfData.source_format === 'markdown'
                        ? 'Source as submitted (.md)'
                        : 'Original PDF'}
                    </span>
                    <ExternalLink className="h-4 w-4" />
                  </a>
                  {pdfData.rendered_pdf_url && (
                    <a
                      href={pdfData.rendered_pdf_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-base font-semibold text-primary-600 hover:text-primary-700 inline-flex items-center space-x-1 transition-colors"
                    >
                      <span>Rendered report (PDF)</span>
                      <ExternalLink className="h-4 w-4" />
                    </a>
                  )}
                </div>
                <p className="text-xs text-grey-400 mt-1.5">
                  {pdfData.source_format === 'markdown' && !pdfData.rendered_pdf_url
                    ? `Rendered PDF is built on first request · links expire in ${pdfData.expires_in_minutes} minutes`
                    : `Link${pdfData.rendered_pdf_url ? 's' : ''} expire${pdfData.rendered_pdf_url ? '' : 's'} in ${pdfData.expires_in_minutes} minutes`}
                </p>
              </div>
            </div>
          )}
        </div>

        {announcement.processing_started_at && (
          <div className="mt-6 pt-6 border-t border-grey-200">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <InfoItem
                icon={<Clock className="h-5 w-5" />}
                label="Processing Started"
                value={format(
                  new Date(announcement.processing_started_at),
                  'MMMM d, yyyy HH:mm:ss'
                )}
              />
              {announcement.processing_completed_at && (
                <InfoItem
                  icon={<CheckCircle className="h-5 w-5" />}
                  label="Processing Completed"
                  value={format(
                    new Date(announcement.processing_completed_at),
                    'MMMM d, yyyy HH:mm:ss'
                  )}
                />
              )}
            </div>
          </div>
        )}

        {announcement.error_message && (
          <div className="mt-6 p-5 bg-danger-50 border border-danger-200 rounded-lg">
            <div className="flex items-start space-x-3">
              <AlertCircle className="h-5 w-5 text-danger-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-danger-700 mb-1">Processing Error</p>
                <p className="text-sm text-danger-600">{announcement.error_message}</p>
              </div>
            </div>
          </div>
        )}

        {pipelineJobs && pipelineJobs.length > 0 && (
          <PipelinePanel jobs={pipelineJobs} />
        )}

        {/* Content Classification */}
        {announcement.editorial_category && (
          <div className="mt-6 pt-6 border-t border-grey-200">
            <div className="flex items-center gap-2 mb-4">
              <Tags className="h-5 w-5 text-grey-500" />
              <h3 className="text-sm font-semibold text-grey-700">Content Classification</h3>
              {announcement.content_classification_confidence != null && (
                <span className="text-xs text-grey-400 ml-auto">
                  {Math.round(announcement.content_classification_confidence * 100)}% confidence
                </span>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              <span className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium bg-primary-50 text-primary-700 border border-primary-200">
                {announcement.editorial_category}
              </span>
              {announcement.editorial_category_secondary && announcement.editorial_category_secondary !== 'null' && (
                <span className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium bg-grey-100 text-grey-600 border border-grey-200">
                  {announcement.editorial_category_secondary}
                </span>
              )}
              {announcement.content_types?.filter(Boolean).map((ct) => (
                <span
                  key={ct}
                  className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium bg-success-50 text-success-700 border border-success-200"
                >
                  {ct.replace(/_/g, ' ')}
                </span>
              ))}
              {announcement.activity_sub_type && announcement.activity_sub_type !== 'null' && (
                <span className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium bg-warning-50 text-warning-700 border border-warning-200">
                  {announcement.activity_sub_type.replace(/_/g, ' ')}
                </span>
              )}
              {announcement.sector && (
                <span className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium bg-info-50 text-info-700 border border-info-200">
                  {announcement.sector}
                </span>
              )}
            </div>
          </div>
        )}

        {/* Processing Actions */}
        <div className="mt-6 pt-6 border-t border-grey-200 flex space-x-4">
          {announcement.processing_status === 'uploaded' && (
            <button
              onClick={() => handleTriggerProcessing(false)}
              disabled={triggerMutation.isPending}
              className="px-6 py-2.5 bg-primary-500 hover:bg-primary-600 text-white font-semibold rounded-lg transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {triggerMutation.isPending ? 'Starting...' : 'Start Processing'}
            </button>
          )}

          {announcement.processing_status === 'failed' && (
            <button
              onClick={() => handleTriggerProcessing(true)}
              disabled={triggerMutation.isPending}
              className="px-6 py-2.5 bg-warning-500 hover:bg-warning-600 text-white font-semibold rounded-lg transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {triggerMutation.isPending ? 'Retrying...' : 'Retry Processing'}
            </button>
          )}

          {announcement.processing_status === 'completed' && (
            <>
              {!showReprocessConfirm ? (
                <button
                  onClick={() => setShowReprocessConfirm(true)}
                  className="px-6 py-2.5 border border-grey-300 text-grey-700 font-semibold rounded-lg hover:bg-grey-50 transition-colors"
                >
                  Reprocess Announcement
                </button>
              ) : (
                <div className="flex flex-col space-y-3">
                  <p className="text-sm text-grey-700">
                    <span className="font-semibold">Reprocess announcement data:</span> Re-extract metadata and images from PDF, and regenerate the default article.
                  </p>
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={forceExtractImages}
                      onChange={(e) => setForceExtractImages(e.target.checked)}
                      className="w-4 h-4 rounded border-grey-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="text-sm text-grey-700">
                      Re-extract images from PDF with AI enrichment
                    </span>
                  </label>
                  <div className="flex items-center space-x-3">
                    <button
                      onClick={() => handleTriggerProcessing(true)}
                      disabled={triggerMutation.isPending}
                      className="px-4 py-2 bg-warning-500 hover:bg-warning-600 text-white font-semibold rounded-lg transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {triggerMutation.isPending ? 'Processing...' : 'Confirm'}
                    </button>
                    <button
                      onClick={() => { setShowReprocessConfirm(false); setForceExtractImages(false); }}
                      className="px-4 py-2 border border-grey-300 text-grey-700 font-semibold rounded-lg hover:bg-grey-50 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </>
          )}

          {announcement.processing_status === 'completed' && id && (
            <ConsolidateControl announcementId={id} />
          )}
        </div>
      </div>

      {/* Extracted Contacts */}
      <AnnouncementContactsCard announcementId={id!} />

      {/* Mentioned Companies / Counterparties */}
      {announcement.intelligence_metadata?.mentioned_companies?.length > 0 && (
        <div className="bg-surface rounded-xl border border-grey-200 shadow-md p-4 sm:p-8">
          <h2 className="text-xl font-bold text-grey-900 mb-4">Mentioned Companies</h2>
          <div className="space-y-3">
            {announcement.intelligence_metadata.mentioned_companies.map((mc: any, idx: number) => (
              <div key={idx} className="flex items-start justify-between p-3 border border-grey-100 rounded-lg">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-grey-900">{mc.company_name}</span>
                    {mc.company_code && (
                      <span className="text-xs px-2 py-0.5 rounded bg-grey-100 text-grey-600 font-mono">
                        {mc.company_code}
                      </span>
                    )}
                    <span className="text-xs px-2 py-0.5 rounded-full bg-primary-50 text-primary-700 font-medium capitalize">
                      {mc.role?.replace(/_/g, ' ')}
                    </span>
                  </div>
                  {mc.context && (
                    <p className="text-sm text-grey-500 mt-1">{mc.context}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Articles */}
      {(announcement.article_count > 0 || generatingArticles.length > 0) && (
        <div className="bg-surface rounded-xl border border-grey-200 shadow-md p-4 sm:p-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-grey-900">
              Generated Articles ({announcement.article_count}
              {generatingArticles.length > 0 && ` + ${generatingArticles.length} generating`})
            </h2>
            <button
              onClick={() => setShowRegenerateDialog(true)}
              className="inline-flex items-center space-x-2 px-5 py-2.5 bg-primary-500 hover:bg-primary-600 text-white font-semibold rounded-lg transition-all shadow-sm"
            >
              <RefreshCw className="h-4 w-4" />
              <span>Generate New Article</span>
            </button>
          </div>

          {(announcement.articles && announcement.articles.length > 0) || generatingArticles.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Show progress cards for articles being generated */}
              {generatingArticles.map((generation, index) => (
                <ArticleProgressCard
                  key={`generating-${index}`}
                  announcementId={id!}
                  style={generation.style}
                  provider={generation.provider}
                  onComplete={(articleId) => {
                    console.log('Article completed:', articleId);
                    queryClient.invalidateQueries({ queryKey: ['announcement', id] });
                  }}
                />
              ))}

              {/* Show existing articles */}
              {announcement.articles?.map((article) => (
                <Link
                  key={article.article_id}
                  to={`/articles/${article.article_id}`}
                  className="p-5 border border-grey-200 rounded-xl hover:border-primary-500 hover:shadow-md transition-all"
                >
                  <div className="flex items-start space-x-3">
                    <Newspaper className="h-5 w-5 text-primary-600 flex-shrink-0 mt-1" />
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-grey-900 line-clamp-2">{article.article_title}</h3>
                      {article.writing_style_id && (
                        <p className="text-sm text-grey-500 mt-1">{article.writing_style_id}</p>
                      )}
                      <div className="flex items-center justify-between mt-3 flex-wrap gap-1">
                        <span className="text-xs text-grey-500 font-medium">{article.word_count} words</span>
                        <div className="flex items-center gap-1 flex-wrap">
                          {articlePublishTargets?.[article.article_id]?.map((pt: any, idx: number) => (
                            <span
                              key={`${pt.target_name}-${idx}`}
                              className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium text-white ${
                                pt.destination_type === 'main_branch'
                                  ? 'bg-success-600'
                                  : 'bg-warning-500'
                              }`}
                            >
                              <Globe className="h-3 w-3" />
                              {pt.target_name} {pt.destination_type === 'main_branch' ? 'live' : 'test'}
                            </span>
                          ))}
                          {!articlePublishTargets?.[article.article_id]?.length && (
                            <>
                              {/* Intended target/destination, derived from the article's
                                  content_product_id (joined on the backend). Shows where
                                  a draft article is aimed so operators don't have to dig
                                  into the detail page to find out. */}
                              {(article as any).intended_target_name && (
                                <span
                                  className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium text-grey-700 bg-grey-100 border border-grey-300"
                                  title={`Intended target (not yet published)`}
                                >
                                  <Globe className="h-3 w-3" />
                                  {(article as any).intended_target_name}
                                  {(article as any).intended_destination_name ? ` ${(article as any).intended_destination_name}` : ''}
                                </span>
                              )}
                              <span
                                className={`text-xs px-2.5 py-1 rounded-full font-medium ${
                                  article.publish_status === 'approved'
                                    ? 'bg-primary-100 text-primary-700'
                                    : 'bg-grey-100 text-grey-600'
                                }`}
                              >
                                {article.publish_status}
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center justify-between mt-3 pt-3 border-t border-grey-200">
                        <span className="text-xs text-grey-400">
                          {format(new Date(article.generation_timestamp), 'MMM d, yyyy HH:mm')}
                        </span>
                        <button
                          onClick={(e) => handleDeleteClick(e, article)}
                          className="p-1.5 text-grey-400 hover:text-danger-600 hover:bg-danger-50 rounded transition-colors"
                          title="Delete article"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <p className="text-grey-500 text-center py-8">
              No articles yet. Click "Generate New Article" to create one.
            </p>
          )}
        </div>
      )}

      {/* Regenerate Article Dialog */}
      <RegenerateArticleDialog
        isOpen={showRegenerateDialog}
        onClose={() => setShowRegenerateDialog(false)}
        announcementId={id!}
        onSuccess={handleRegenerateSuccess}
        onGenerationStart={(style, provider) => {
          console.log('Starting article generation:', style, provider);
          startGenerating(style, provider);
        }}
      />

      {/* Delete Confirmation Dialog */}
      {deleteConfirm && (
        <Modal
          onClose={() => setDeleteConfirm(null)}
          canClose={!deleteMutation.isPending}
          size="md"
        >
          <ModalBody>
            <h3 className="text-xl font-bold text-grey-900 mb-4">Delete Article?</h3>
            <p className="text-grey-600 mb-2">
              Are you sure you want to delete this article? This action cannot be undone.
            </p>
            <p className="text-grey-900 font-medium p-3 bg-grey-50 rounded border border-grey-200">
              {deleteConfirm.articleTitle}
            </p>
          </ModalBody>
          <ModalFooter>
            <button
              onClick={() => setDeleteConfirm(null)}
              disabled={deleteMutation.isPending}
              className="flex-1 px-4 py-2.5 border-2 border-grey-300 text-grey-700 font-semibold rounded-lg hover:bg-grey-50 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={confirmDelete}
              disabled={deleteMutation.isPending}
              className="flex-1 px-4 py-2.5 bg-danger-600 hover:bg-danger-700 text-white font-semibold rounded-lg transition-colors disabled:opacity-50"
            >
              {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
            </button>
          </ModalFooter>
        </Modal>
      )}
    </div>
  );
}

function InfoItem({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) {
  return (
    <div className="flex items-start space-x-3">
      <div className="text-grey-400 mt-0.5">{icon}</div>
      <div>
        <p className="text-sm text-grey-500 font-medium mb-1">{label}</p>
        <p className="text-base font-semibold text-grey-900">{value}</p>
      </div>
    </div>
  );
}

// Per-stage status colours for the pipeline timeline. Mirror the
// pipeline_jobs.status enum: pending|running|completed|failed|retrying.
const JOB_STATUS_COLORS: Record<string, string> = {
  pending:   'bg-grey-100 text-grey-700 border-grey-200',
  running:   'bg-purple-100 text-purple-700 border-purple-200',
  retrying:  'bg-warning-100 text-warning-700 border-warning-200',
  completed: 'bg-success-100 text-success-700 border-success-200',
  failed:    'bg-danger-100 text-danger-700 border-danger-200',
};

function PipelinePanel({ jobs }: { jobs: PipelineJobSummary[] }) {
  return (
    <div className="mt-6 pt-6 border-t border-grey-200">
      <div className="flex items-center gap-2 mb-4">
        <RefreshCw className="h-5 w-5 text-grey-500" />
        <h3 className="text-sm font-semibold text-grey-700">Pipeline</h3>
        <span className="text-xs text-grey-400 ml-auto">
          {jobs.length} stage{jobs.length !== 1 ? 's' : ''}
        </span>
      </div>
      <div className="space-y-2">
        {jobs.map(j => {
          const colour = JOB_STATUS_COLORS[j.status] || JOB_STATUS_COLORS.pending;
          const tookMs = new Date(j.updated_at).getTime() - new Date(j.created_at).getTime();
          const tookLabel = j.status === 'completed' || j.status === 'failed'
            ? `${(tookMs / 1000).toFixed(1)}s`
            : null;
          return (
            <div key={j.job_id} className="rounded-lg border border-grey-200 bg-surface p-3">
              <div className="flex items-center gap-3 flex-wrap">
                <span className="font-mono text-sm font-semibold text-grey-900">{j.job_type}</span>
                <span className={`px-2 py-0.5 text-[10px] font-semibold rounded-full border ${colour}`}>
                  {j.status}
                </span>
                <span className="text-xs text-grey-500">
                  attempt {j.attempts}/{j.max_attempts}
                </span>
                {tookLabel && (
                  <span className="text-xs text-grey-400">· {tookLabel}</span>
                )}
                <span className="ml-auto text-xs text-grey-400">
                  {format(new Date(j.updated_at), 'd MMM yy HH:mm:ss')}
                </span>
              </div>
              {j.error_message && (
                <p className="mt-2 text-xs text-danger-600 font-mono whitespace-pre-wrap break-words">
                  {j.error_message}
                </p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
